import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useListKeyboardNavigation } from '@/shared/hooks/useListKeyboardNavigation';
import {
  applyDisabledValues,
  type CascaderOption,
  type CascaderProps,
  ShowCheckedStrategy,
} from '../helpers';
import { getDisplayPaths, getDisplayText } from '../utils/display-utils';
import { getSelectedOptions } from '../utils/path-utils';
import { type SearchResult, searchOptionsFlat } from '../utils/search-utils';
import {
  consolidatePaths,
  expandConsolidatedPaths,
  getLongestPath,
  removeBranch,
  toggleBranchSelection,
} from '../utils/selection-utils';

type UseCascaderProps = Pick<
  CascaderProps,
  | 'options'
  | 'value'
  | 'onChange'
  | 'showSearch'
  | 'changeOnSelect'
  | 'displayRender'
  | 'multiple'
  | 'showCheckedStrategy'
  | 'maxTagCount'
  | 'disabledItemValues'
  | 'disabledItemReasons'
  | 'onlyLeafSelectable'
>;

/**
 * Cascader コンポーネントのロジックを管理するカスタムフック
 *
 * 状態管理、イベントハンドラー、計算値を提供します
 */
export const useCascader = ({
  options,
  value = [],
  onChange,
  showSearch = false,
  changeOnSelect = false,
  displayRender,
  multiple = false,
  showCheckedStrategy = ShowCheckedStrategy.SHOW_CHILD,
  maxTagCount,
  disabledItemValues = [],
  disabledItemReasons,
  onlyLeafSelectable = true,
}: UseCascaderProps) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activePath, setActivePath] = useState<string[]>([]);
  const [selectedPaths, setSelectedPaths] = useState<string[][]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // disabledItemValuesを適用したオプション
  // useEffectの依存配列で使用されるため、useMemoで参照を安定化する
  const processedOptions = useMemo(
    () => applyDisabledValues(options, disabledItemValues, disabledItemReasons),
    [options, disabledItemValues, disabledItemReasons],
  );

  // 検索結果（useListKeyboardNavigation に itemCount を渡すため先に計算）
  const searchResults =
    showSearch && searchValue ? searchOptionsFlat(processedOptions, searchValue) : [];
  const isSearching = showSearch && searchValue.length > 0 && searchResults.length > 0;

  // value が変更されたら state を更新
  useEffect(() => {
    if (multiple) {
      // 複数選択モード: value が string[][] 形式であることを確認
      if (Array.isArray(value) && (value.length === 0 || Array.isArray(value[0]))) {
        // 統合パス（親のみ）の場合はフル展開に戻す
        const expanded = expandConsolidatedPaths(value as string[][], processedOptions);
        setSelectedPaths(expanded);

        // 複数選択モードでは、選択済みパスの最長パスに基づいて初期階層を展開
        if (expanded.length > 0) {
          const longestPath = getLongestPath(expanded);
          setActivePath(longestPath.slice(0, -1));
        }
      } else {
        setSelectedPaths([]);
      }
    } else {
      // 単一選択モード: value が string[] 形式であることを確認
      if (Array.isArray(value) && !Array.isArray(value[0])) {
        setActivePath(value as string[]);
      } else {
        setActivePath([]);
      }
    }
  }, [value, multiple, processedOptions]);

  // combobox パターン: Popover 開閉時にトリガー input にフォーカスを維持
  // （トリガー自体が input なので、Popover 開時にフォーカスが移動しないことを保証）

  // 検索値変更ハンドラー（activeIndex リセット付き）
  const handleSearchValueChange = (nextSearchValue: string) => {
    setSearchValue(nextSearchValue);
    resetActiveIndex();
  };

  // Popover開閉ハンドラー
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    // Popoverを閉じたとき、未確定のactivePathをvalueにリセット
    if (!newOpen && !multiple) {
      if (Array.isArray(value) && !Array.isArray(value[0])) {
        setActivePath(value as string[]);
      } else {
        setActivePath([]);
      }
      setSearchValue('');
      resetActiveIndex();
    }
    if (!newOpen && multiple) {
      resetActiveIndex();
    }
  };

  // 値があるかどうかを判定
  const hasValue = multiple ? selectedPaths.length > 0 : activePath.length > 0;

  // 統合済みパスで onChange を呼び出すヘルパー
  const emitConsolidatedChange = (newPaths: string[][]) => {
    const consolidated = consolidatePaths(newPaths, processedOptions);
    onChange?.(
      consolidated,
      consolidated.map((p) => getSelectedOptions(p, processedOptions)),
    );
  };

  // 複数選択時のタグ削除
  const handleRemoveTag = (pathToRemove: string[]) => {
    const newPaths = removeBranch(selectedPaths, pathToRemove);
    setSelectedPaths(newPaths);
    emitConsolidatedChange(newPaths);
  };

  // クリアボタンのハンドラー
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (multiple) {
      setSelectedPaths([]);
      setActivePath([]);
      onChange?.([], []);
    } else {
      setActivePath([]);
      onChange?.([], []);
    }
  };

  // チェックボックスのトグル処理（複数選択専用）
  const handleCheckboxToggle = (option: CascaderOption, level: number) => {
    if (option.disabled) return;

    const newPath = [...activePath.slice(0, level), option.value];
    const newPaths = toggleBranchSelection(selectedPaths, newPath, option);

    setSelectedPaths(newPaths);
    emitConsolidatedChange(newPaths);
  };

  // 階層移動処理
  const handleNavigate = (option: CascaderOption, level: number) => {
    if (option.disabled) return;

    const newPath = [...activePath.slice(0, level), option.value];

    if (multiple) {
      // 複数選択モード: 子要素がある場合のみ階層移動
      if (option.children && option.children.length > 0) {
        setActivePath(newPath);
      }
    } else {
      // 単一選択モード
      setActivePath(newPath);

      const isLeaf = !option.children || option.children.length === 0;
      const shouldSelect = onlyLeafSelectable ? isLeaf : isLeaf || changeOnSelect;

      if (shouldSelect) {
        const selectedOptions = getSelectedOptions(newPath, processedOptions);
        onChange?.(newPath, selectedOptions);

        if (isLeaf) {
          setOpen(false);
          setSearchValue('');
        }
      }
    }
  };

  // 検索結果から選択
  const handleSelectSearchResult = (result: SearchResult) => {
    if (result.option.disabled) return;

    if (multiple) {
      const newPath = result.path;
      const newPaths = toggleBranchSelection(selectedPaths, newPath, result.option);

      setSelectedPaths(newPaths);
      emitConsolidatedChange(newPaths);

      setSearchValue('');
      resetActiveIndex();
      setActivePath(result.path.slice(0, -1));
    } else {
      const selectedOptions = getSelectedOptions(result.path, processedOptions);
      onChange?.(result.path, selectedOptions);
      setOpen(false);
      setSearchValue('');
      resetActiveIndex();
    }
  };

  // 検索時のキーボードナビゲーション
  const {
    activeIndex,
    handleKeyDown: handleSearchKeyDown,
    resetActiveIndex,
  } = useListKeyboardNavigation({
    itemCount: searchResults.length,
    onSelect: (index) => {
      const result = searchResults[index];
      if (result) {
        handleSelectSearchResult(result);
      }
    },
    onClose: () => {
      handleOpenChange(false);
    },
    enabled: isSearching,
  });

  // Popover 全体のキーボードハンドラー（非検索時用）
  const handleKeyDown = (_e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
  };

  // ホバー状態管理
  const handleMouseEnter = (level: number, idx: number) => {
    const columnCount = activePath.length + 1;
    const newHovered = Array(columnCount).fill(-1);
    newHovered[level] = idx;
    setHoveredIndex(newHovered);
  };

  const handleMouseLeave = (level: number) => {
    const newHovered = [...hoveredIndex];
    newHovered[level] = -1;
    setHoveredIndex(newHovered);
  };

  // 表示テキスト
  const displayText = getDisplayText(activePath, processedOptions, displayRender);

  // 表示パスリスト
  const displayPathsList = multiple
    ? getDisplayPaths(selectedPaths, processedOptions, showCheckedStrategy)
    : [];

  // maxTagCount の処理
  const visibleTags =
    maxTagCount && typeof maxTagCount === 'number'
      ? displayPathsList.slice(0, maxTagCount)
      : displayPathsList;
  const remainingCount = displayPathsList.length - visibleTags.length;

  // 表示するカラム数
  const columnCount = activePath.length + 1;

  return {
    // 状態
    open,
    searchValue,
    activePath,
    selectedPaths,
    hoveredIndex,
    activeIndex,
    inputRef,

    // 計算値
    processedOptions,
    hasValue,
    displayText,
    visibleTags,
    remainingCount,
    columnCount,
    searchResults,
    isSearching,

    // セッター
    setSearchValue: handleSearchValueChange,

    // ハンドラー
    handleOpenChange,
    handleRemoveTag,
    handleClear,
    handleCheckboxToggle,
    handleNavigate,
    handleSelectSearchResult,
    handleSearchKeyDown,
    handleKeyDown,
    handleMouseEnter,
    handleMouseLeave,
  };
};
